import React, { useState } from "react";
import { useFormik } from "formik";
import { axiosInstance } from "../service/axiosInstance";
import { useAuth } from "../context/AuthProvider";
import dayjs from "dayjs";
import InputAdornment from "@mui/material/InputAdornment";

import {
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  FormHelperText,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import TagMeetingHeader from "../components/TagMeetingHeader";
import ThankYouComponent from "../components/ThankYouComponent";

import { addConveyanceSchema } from "../utils/validation";
import constant from "../utils/constants";
import serviceEndpoints from "../services/serviceEndpoints";

const AddConveyance = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [meetingTypeDataList, setMeetingTypeDataList] = useState([]);
  const [meetingData, setmeetingData] = useState({});
  const { userDetail } = useAuth();

  const { handleSubmit, touched, errors, values, setFieldValue } = useFormik({
    initialValues: {
      date: null,
      meeting: "",
      vehical_type: "",
      calculate_fare: "",
    },
    onSubmit: (values) => handleAddConveyance(values),
    validationSchema: addConveyanceSchema,
  });

  const handleAddConveyance = async (values) => {
    let payload = {
      code: meetingData.code,
      vehicleType: values.vehical_type,
    };
    try {
      await axiosInstance.post(serviceEndpoints.addConveyanceEndpoint, {
        ...payload,
      });

      setShowSuccess(true);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDateChange = async (date) => {
    try {
      const formattedDate = date.format(constant.backendDateFormat);

      const response = await axiosInstance.post(
        serviceEndpoints.searchMeetingEndpoint,
        {
          date: formattedDate,
        }
      );

      setMeetingTypeDataList(response.data.meetings);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleMeetingTypeChange = (selectedValue) => {
    setFieldValue("meeting", selectedValue);
    const imdCode = selectedValue.match(/\(([^)]+)\)/)?.[1];

    if (!imdCode) {
      console.error("Invalid value format. IMD Code could not be extracted.");
      return;
    }

    const meetingDetails = meetingTypeDataList.find(
      (item) => item.imd_code === imdCode
    );
    if (values.vehical_type) {
      setFieldValue(
        "calculate_fare",
        meetingDetails?.distance_travelled *
          userDetail.meta.CONVEYANCE_MULTIPLICATION_FACTORS[values.vehical_type]
      );
    }
    setmeetingData(meetingDetails);
  };

  const handleVehicleTypeChange = (value) => {
    setFieldValue("vehical_type", value);

    setFieldValue(
      "calculate_fare",
      meetingData?.distance_travelled *
        userDetail.meta.CONVEYANCE_MULTIPLICATION_FACTORS[value]
    );
  };

  return (
    <>
      <TagMeetingHeader headerName="Add conveyance" />
      {showSuccess ? (
        <ThankYouComponent
          message={"Thank you, your conveyance has been added"}
        />
      ) : (
        <Container maxWidth="sm" sx={{ marginTop: 2, paddingBottom: "80px" }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <Box
              sx={{
                alignItems: "center",
                borderRadius: "30px",
                padding: 2,
                // border: "1px solid #ddd",
                flexDirection: "row",
              }}
              className="container_bg"
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Typography
                  variant="h6"
                  className="typography_heading form_sub_head"
                >
                  Select date <span className="mandatory_mark"> *</span>
                </Typography>
                <DatePicker
                  value={values.date}
                  format="DD-MM-YYYY"
                  onChange={(newValue) => {
                    setFieldValue("date", newValue);
                    if (newValue) {
                      handleDateChange(newValue);
                    }
                  }}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: "DD-MM-YYYY",
                      error: Boolean(touched.date && errors.date),
                      helperText:
                        touched.date && errors.date ? errors.date : null,
                    },
                  }}
                />
              </LocalizationProvider>

              <Box sx={{ marginY: 2 }}>
                <Typography
                  variant="h6"
                  className="typography_heading form_sub_head"
                >
                  Meeting type <span className="mandatory_mark"> *</span>
                </Typography>
                <FormControl
                  fullWidth
                  error={touched.meeting && Boolean(errors.meeting)}
                >
                  <InputLabel>Select meeting</InputLabel>
                  <Select
                    name="meeting"
                    label="Select Meeting"
                    disabled={
                      meetingTypeDataList && meetingTypeDataList.length === 0
                    }
                    value={values.meeting}
                    required={true}
                    onChange={(e) => handleMeetingTypeChange(e.target.value)}
                    sx={{
                      textAlign: "left",
                      "& .MuiSelect-select": {
                        textAlign: "left",
                      },
                    }}
                    error={touched.meeting && Boolean(errors.meeting)}
                    helperText={touched.meeting && errors.meeting}
                  >
                    {meetingTypeDataList.map((item, index) => (
                      <MenuItem
                        key={index}
                        value={`${item.imd_name} (${item.imd_code})`}
                      >
                        {`${item.imd_name} (${item.imd_code})`}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {touched.meeting && errors.meeting}
                  </FormHelperText>
                </FormControl>
              </Box>

              <Box sx={{ marginY: 2 }}>
                <Typography
                  variant="h6"
                  className="typography_heading form_sub_head"
                >
                  Vehicle type <span className="mandatory_mark"> *</span>
                </Typography>
                <RadioGroup
                  row
                  name="vehicle_type"
                  sx={{
                    "& .MuiSvgIcon-root": {
                      fontSize: "25px !important",
                      color:
                        touched.dayType && errors.dayType ? "red" : "inherit",
                    },
                  }}
                  value={values.vehical_type}
                  onChange={(e) => handleVehicleTypeChange(e.target.value)}
                >
                  <FormControlLabel
                    value="Car"
                    control={
                      <Radio
                        sx={{
                          color:
                            touched.vehical_type && errors.vehical_type
                              ? "red"
                              : "inherit", // Border color for error
                          "&.Mui-checked": {
                            color:
                              touched.vehical_type && errors.vehical_type
                                ? "red"
                                : "primary.main", // Change checked color on error
                          },
                        }}
                      />
                    }
                    label="Car"
                  />
                  <FormControlLabel
                    value="Bike"
                    control={
                      <Radio
                        sx={{
                          color:
                            touched.vehical_type && errors.vehical_type
                              ? "red !important"
                              : "inherit", // Border color for error
                          "&.Mui-checked": {
                            color:
                              touched.vehical_type && errors.vehical_type
                                ? "red !important"
                                : "primary.main", // Change checked color on error
                          },
                        }}
                      />
                    }
                    label="Bike"
                  />
                </RadioGroup>
                {touched.vehical_type && errors.vehical_type && (
                  <Typography
                    variant="caption"
                    color="error"
                    component="div"
                    sx={{ textAlign: "left", marginTop: "4px" }}
                  >
                    {errors.vehical_type}
                  </Typography>
                )}
              </Box>
              {Object.keys(meetingData).length !== 0 && (
                <>
                  <Box sx={{ marginBottom: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "15px",
                        marginBottom: "8px",
                      }}
                      className="typography_heading form_sub_head"
                    >
                      Calculated Fare
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      disabled
                      value={values.calculate_fare || 0}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      backgroundColor: "#f5f5f5",
                      padding: 2,
                      borderRadius: "30px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <span style={{ fontWeight: "bold" }}>From:</span>
                        {meetingData.start_location_address}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <span style={{ fontWeight: "bold" }}>To:</span>{" "}
                        {meetingData.meeting_address}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, textAlign: "left" }}
                    >
                      <span style={{ fontWeight: "bold" }}>Meeting with:</span>{" "}
                      {meetingData.meeting_with}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, textAlign: "left" }}
                    >
                      <span style={{ fontWeight: "bold" }}>Purpose:</span>{" "}
                      {meetingData.purpose}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "left" }}>
                      <span style={{ fontWeight: "bold" }}>
                        Calculated Kms:
                      </span>{" "}
                      {meetingData.distance_travelled}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
            <Box
              sx={{
                // position: "absolute",
                bottom: 16,
                left: 0,
                width: "100%",
                padding: "0 16px",
                marginTop: "10px",
              }}
            >
              <Button type="submit" variant="contained" fullWidth>
                Submit
              </Button>
            </Box>
          </form>
        </Container>
      )}
    </>
  );
};

export default AddConveyance;
